var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Subject } from '@/models/Subject';
import AsyncSelect from '@/components/async-select/AsyncSelect';
import { TreatmentLabel } from '@/models/TreatmentLabel';
import { Student } from '@/models/Student';
import ErrorHandler from '@/support/ErrorHandler';
import { Treatment as TreatmentModel } from '@/models/Treatment';
import { Lvs } from '@/models/Lvs';
import LoaderCard from '@/components/loader-card/LoaderCard';
import AInlineEditable from '@/components/InlineEditable/InlineEditable';
import EditableTextarea from '@/components/EditableTextarea/EditableTextarea';
import AQuillEditor from '@/components/AQuillEditor/AQuillEditor.vue';
let TreatmentDialog = class TreatmentDialog extends Vue {
    constructor() {
        // File structure for Typescript files in your Vue project
        // https://tutorials.atabix.com/frontend/typscript_file_structure/
        super(...arguments);
        // #region @Props
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        // protected onDebounceLvsDescription = debounce(this.onUpdateLvsDescription, 1000)
        this.cachedLvsDescriptions = {};
        this.student = new Student();
        this.selectedSubject = null;
        this.subject = new Subject();
        this.content = '';
        this.isSaving = false;
        this.isLoading = true;
        this.lvsDescription = '';
        this.areas = [];
        this.isLoadingLvs = true;
        this.treatmentLabels = [];
        this.selectedTreatments = [];
        this.treatmentLabelsLoaded = false;
        this.isDuplicatedSubjectId = false;
    }
    // #endregion
    // #region Lifecycle Hooks / Init
    // #endregion
    // #region Class methods
    // #endregion
    // #region Async methods
    // #endregion
    // #region Getters & Setters
    // #endregion
    // #region @Watchers
    // #endregion
    get dialogOptions() {
        return {
            title: this.$tc('dialogOptions.confirmation'),
            text: this.$tc('dialogOptions.save', 0, { item: this.$tc('dialogOptions.item.treatment') }),
            type: 'success',
            buttons: {
                confirm: {
                    text: this.$tc('dialogOptions.button.ok'),
                },
            },
        };
    }
    get hasAreas() {
        return !!this.areas.length;
    }
    mounted() {
        this.initialize();
    }
    async initialize() {
        await this.fetchTreatmentLabels();
        await this.fetchUserLvs();
        await this.fetchStudent();
        if (this.subjectId) {
            await this.fetchSubject();
        }
    }
    async fetchStudent() {
        if (!this.student) {
            return;
        }
        await new Student()
            .include(['treatment_labels', 'treatment'])
            .find(this.studentId)
            .then((student) => {
            this.student = student;
            this.setSelectedTreatments();
            this.setTreatment();
            this.isLoading = false;
            this.treatmentLabelsLoaded = true;
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    onEditorChange(quill, html) {
        this.content = html;
    }
    async save() {
        this.isSaving = true;
        const lvsDescriptions = Object.values(this.cachedLvsDescriptions);
        if (lvsDescriptions.length) {
            const promises = lvsDescriptions.map(async ({ event, area }) => this.updateLvsDescription(event, area));
            await Promise.all(promises);
        }
        // Todo: Boolean is saving, hier true en in then en catch op false
        const treatmentLabels = [];
        this.selectedTreatments.forEach((id) => {
            treatmentLabels.push({ id });
        });
        await this.student.updateTreatmentLabels(treatmentLabels).catch((error) => {
            this.setSelectedTreatments();
            ErrorHandler.network(error);
        });
        if (this.subjectId) {
            await this.onUpdateDescription();
        }
        const treatmentPayload = {
            user: this.student && this.student.id ? this.student.id : '',
            description: this.content,
        };
        await new TreatmentModel().updateTreatment(treatmentPayload).catch((error) => {
            this.setTreatment();
            ErrorHandler.network(error);
        });
        this.$store.dispatch('openDialog', this.dialogOptions);
        this.$emit('treatment-saved', this.treatmentLabels.filter((treatmentLabel) => this.selectedTreatments.includes(treatmentLabel.id || '')));
        this.isSaving = false;
    }
    setSelectedTreatments() {
        if (!this.student || !this.student.treatment_labels) {
            return;
        }
        this.selectedTreatments = this.student.treatment_labels.map((treatmentLabel) => {
            return treatmentLabel.id;
        });
    }
    setTreatment() {
        if (!this.student || !this.student.treatment) {
            return;
        }
        this.content = this.student.treatment.description || '';
    }
    closeDialog() {
        this.$emit('closed');
    }
    async fetchTreatmentLabels() {
        this.treatmentLabelsLoaded = false;
        await new TreatmentLabel().all().then((treatmentLabels) => {
            this.treatmentLabels = [...[], ...treatmentLabels];
            this.treatmentLabelsLoaded = true;
        });
    }
    async fetchSubject() {
        this.isLoading = true;
        await new Subject()
            .include(['chat', 'curriculum', 'description', 'level', 'period', 'children', 'meta', 'parent'])
            .find(this.subjectId)
            .then(async (subject) => {
            // console.log('fetchSubject', subject);
            this.subject = subject;
            await this.fetchLvsDescription();
            this.isLoading = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    async fetchLvsDescription() {
        await new Lvs()
            .filter({ areas: this.subject.id ? [this.subject.id] : [], users: [this.studentId] })
            .all()
            .then((response) => {
            const subject = response.find((subject) => subject.id === this.subject.id) || null;
            this.lvsDescription = subject ? subject.users[this.studentId].notes : '';
        });
    }
    async onUpdateDescription() {
        const area = (this.subject.parent && this.subject.parent.id) || '';
        await this.student
            .setLvsAreasEndpoint()
            .create({
            subject_id: this.subjectId || area,
            notes: this.lvsDescription,
        })
            .catch((error) => ErrorHandler.network(error));
    }
    async fetchUserLvs() {
        this.isLoadingLvs = true;
        new Lvs()
            .filter({ users: [this.studentId] })
            .all()
            .then((result) => {
            this.isDuplicatedSubjectId = !!((result && result.find((area) => area.id === this.subjectId)) || false);
            this.areas = result;
            this.isLoadingLvs = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
            this.isLoadingLvs = false;
        });
    }
    async updateLvsDescription(event, area) {
        if (!this.studentId) {
            return;
        }
        event.setLoading(true);
        const response = await new Student({ id: this.studentId })
            .setLvsAreasEndpoint()
            .create({
            subject_id: area.id,
            notes: event.value,
        })
            .catch(() => {
            event.setLoading(false);
            return;
        });
        if (area && area.users) {
            area.users[this.studentId].notes = response.notes;
            event.setSuccess(true);
            event.setLoading(false);
            setTimeout(() => {
                event.setSuccess(false);
            }, 500);
            return;
        }
    }
    close() {
        this.$emit('input', false);
    }
};
__decorate([
    Prop(),
    __metadata("design:type", String)
], TreatmentDialog.prototype, "studentId", void 0);
__decorate([
    Prop({ default: null }),
    __metadata("design:type", String)
], TreatmentDialog.prototype, "subjectId", void 0);
TreatmentDialog = __decorate([
    Component({
        components: {
            AsyncSelect,
            LoaderCard,
            AQuillEditor,
            AInlineEditable,
            EditableTextarea,
        },
    })
], TreatmentDialog);
export default TreatmentDialog;
